<template>
  <div class="me-page">
    <h1>Mein Profil</h1>

    <div v-if="!ready" class="state-box">
      Lade Profildaten...
    </div>

    <div v-else-if="error" class="state-box error">
      {{ error }}
    </div>

    <template v-else-if="user">
      <section class="profile-card profile-header-card">
        <h2>Profilübersicht</h2>

        <div class="profile-header">
          <div class="avatar-wrap">
            <img
              class="avatar"
              :src="resolvedAvatarUrl"
              @error="onAvatarError"
              alt="Profilbild"
            />
            <div v-if="uploadingAvatar" class="avatar-progress">
              <div class="bar">
                <div class="fill" :style="{ width: avatarProgress + '%' }"></div>
              </div>
              <span class="percent">{{ avatarProgress }}%</span>
            </div>
            <div class="avatar-overlay">
              <button type="button" class="avatar-upload-btn" @click="avatarInput?.click()">
                Upload
              </button>
            </div>

            <input
              ref="avatarInput"
              type="file"
              accept="image/*"
              class="avatar-input"
              @change="handleAvatarSelected"
            />
          </div>

          <div class="profile-info">
            <div class="profile-item compact">
              <span class="label">E-Mail</span>
              <span class="value">{{ user.email || '-' }}</span>
            </div>

            <div class="profile-item compact">
              <span class="label">Guthaben</span>
              <span class="value">{{ balanceFormatted }} €</span>
            </div>

            <div class="profile-item compact" v-if="user.id">
              <span class="label">Benutzer-ID</span>
              <span class="value">{{ user.id }}</span>
            </div>
          </div>
        </div>

        <div class="demo-balance-box">
          <h3>Guthaben Aufladen</h3>
          <p>
            Bitte wähle aus, wie viel Geld du einzahlen willst:
          </p>
          <div class="demo-buttons">
            <button type="button" class="demo-button" @click="addBalance(10)">+10 €</button>
            <button type="button" class="demo-button" @click="addBalance(50)">+50 €</button>
            <button type="button" class="demo-button" @click="addBalance(100)">+100 €</button>
          </div>
        </div>
        <div class="demo-balance-box">
          <h3>Abrechnung</h3>
          <p>
            Lade einen Nachweis über alle Wetten als PDF herunter.
          </p>
          <button type="button" class="demo-button" @click="downloadPdf">PDF herunterladen</button>
        </div>
      </section>

      <section class="profile-card">
        <h2>Aktive Wetten</h2>

        <div v-if="betsLoading" class="state-box">
          Lade deine Wetten...
        </div>

        <div v-else-if="activeBets.length === 0" class="state-box">
          Du hast keine aktiven Wetten.
        </div>

        <div v-else class="bets-list">
          <article v-for="bet in activeBets" :key="bet.id || bet.wetteId" class="bet-item">
            <div class="bet-top">
              <router-link :to="'/wetten/' + bet.id" class="bet-title-link">
                <h3>
                  {{
                    bet.title ||
                    bet.name ||
                    ('Wette #' + (bet.id || bet.wetteId))
                  }}
                </h3>
              </router-link>

              <span class="bet-status" :class="bet.status">
                {{ bet.status || 'offen' }}
              </span>
            </div>

            <p class="bet-desc">
              {{ bet.description || bet.text || 'Keine Beschreibung vorhanden.' }}
            </p>
            <div class="my-stakes-list">
              <h4>Deine Einsätze:</h4>
              <BetMyStakesTable :my-enriched-bets="bet.enrichedStakes" :total-stake="formatMoney(bet.myTotal ?? 0)" :show-title="false" @increase="(entry) => handleIncreaseStake(bet, entry)"/>
            </div>
          </article>
        </div>
      </section>

      <section class="profile-card">
        <h2>Abgeschlossene Wetten</h2>

        <div v-if="betsLoading" class="state-box">
          Lade deine Wetten...
        </div>

        <div v-else-if="finishedBets.length === 0" class="state-box">
          Du hast keine abgeschlossenen Wetten.
        </div>

        <div v-else class="bets-list">
          <article v-for="bet in finishedBets" :key="bet.id || bet.wetteId" class="bet-item">
            <div class="bet-top">
              <router-link :to="'/wetten/' + bet.id" class="bet-title-link">
                <h3>
                  {{
                    bet.title ||
                    bet.name ||
                    ('Wette #' + (bet.id || bet.wetteId))
                  }}
                </h3>
              </router-link>

              <span class="bet-status" :class="getBetStatus(bet).key">{{ getBetStatus(bet).label }}</span>
            </div>

            <p class="bet-desc">
              {{ bet.description || bet.text || 'Keine Beschreibung vorhanden.' }}
            </p>

            <div class="my-stakes-list">
              <h4>Deine Einsätze:</h4>
              <BetMyStakesTable :my-enriched-bets="bet.enrichedStakes" :total-stake="formatMoney(bet.myTotal ?? 0)" :show-title="false"/>
            </div>
          </article>
        </div>
      </section>

      <section class="settings-grid">
        <form class="profile-card" @submit.prevent="handleChangePassword">
          <h2>Passwort ändern</h2>

          <div class="form-group">
            <label for="currentPassword">Aktuelles Passwort</label>
            <input id="currentPassword" v-model="passwordForm.currentPassword" type="password" required />
          </div>

          <div class="form-group">
            <label for="newPassword">Neues Passwort wiederholen</label>

            <input id="newPassword" v-model="passwordForm.newPassword" type="password" required />
          </div>

          <button type="submit" :disabled="saving">
            Passwort ändern
          </button>
        </form>

        <form class="profile-card" @submit.prevent="handleChangeEmail">
          <h2>E-Mail ändern</h2>

          <div class="form-group">
            <label for="newEmail">Neue E-Mail</label>

            <input id="newEmail" v-model="emailForm.newEmail" type="email" required />
          </div>

          <div class="form-group">
            <label for="emailPassword">Passwort bestätigen</label>

            <input id="emailPassword" v-model="emailForm.password" type="password" required />
          </div>

          <button type="submit" :disabled="saving">
            E-Mail ändern
          </button>
        </form>

        <div class="profile-card danger-card">
          <h2>Konto löschen</h2>

          <p>
            Das löscht dein Konto dauerhaft.
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>

          <div class="form-group">
            <label for="deletePassword">Passwort bestätigen</label>

            <input id="deletePassword" v-model="deleteForm.password" type="password" required />
          </div>

          <div class="form-group">
            <label for="deleteConfirm">Zur Bestätigung tippe DELETE</label>

            <input id="deleteConfirm" v-model="deleteForm.confirmText" type="text" required />
          </div>

          <button class="danger" type="button" :disabled="saving" @click="handleDeleteAccount">
            Konto löschen
          </button>
        </div>
      </section>

      <p v-if="successMessage" class="message success">
        {{ successMessage }}
      </p>

      <p v-if="actionError" class="message error">
        {{ actionError }}
      </p>
    </template>

    <div v-else class="state-box error">
      Kein Benutzer geladen.
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'

import { DEFAULT_PROFILE_IMAGE } from '@/assets/config'
// Helferfunktion, um bei Bild-Ladefehlern auf das Standard-Avatar zurückzufallen.
import { onImageError } from '@/utils/image'

import BetMyStakesTable from "@/views/BetMyStakesTable.vue"

import {
  uploadFile, resolveApiUrl,
  getMyBets,
  changePassword,
  changeEmail,
  deleteAccount,
  balanceAufladen,
  downloadWettenPdf,
  placeBet
} from '../services/api'

// Composables und globale Zustände
const router = useRouter()
const { refreshAuth, user, ready, logout } = useAuth()

// Lokale Zustandsvariablen der Komponente
const betsLoading = ref(true)
const saving = ref(false)
const error = ref('')
const actionError = ref('')
const successMessage = ref('')
const myBets = ref([])
const avatarInput = ref(null)
const avatarPreview = ref('')

// Reaktive Objekte für die Formulareingaben
const passwordForm = reactive({
  currentPassword: '',
  newPassword: ''
})

const emailForm = reactive({
  newEmail: '',
  password: ''
})

const deleteForm = reactive({
  password: '',
  confirmText: ''
})

// Berechnete Eigenschaft, um das Guthaben formatiert anzuzeigen.
const balanceFormatted = computed(() => {
  const value = Number(user.value?.balance ?? 0)
  return value.toFixed(2)
})

// Berechnete Eigenschaft, um die Avatar-URL aufzulösen.
// Verwendet eine lokale Vorschau während des Uploads, ansonsten das Server-Bild oder den Standard.
const resolvedAvatarUrl = computed(() => {
  const raw = avatarPreview.value || user.value?.profileimage || DEFAULT_PROFILE_IMAGE
  console.log("resolvedAvatarUrl", user, raw, resolveApiUrl(raw));
  return resolveApiUrl(raw)
})

/**
 * Löst den Download des PDF-Wettennachweises aus.
 */
const downloadPdf = async () => {
  clearMessages()
  try {
    await downloadWettenPdf()
  } catch (e) {
    actionError.value = e.message || 'PDF konnte nicht heruntergeladen werden.'
  }
}

/**
 * Formatiert einen numerischen Wert als Geldbetrag.
 * @param {number | string} value - Der zu formatierende Wert.
 * @returns {string} Der formatierte String (z.B. "10.00").
 */
const formatMoney = (value) => {
  return Number(value ?? 0).toFixed(2)
}

const enrichedMyBets = computed(() => {
  if (!myBets.value) return []
  return myBets.value.map((bet) => {
    const myBetsArr = bet.myBets ?? bet.my_bets ?? []
    const positionsArr = bet.positions ?? bet.positionen ?? bet.pos ?? []
    if (!myBetsArr.length || !positionsArr.length) {
      return { ...bet, enrichedStakes: [] }
    }
    const enrichedStakes = myBetsArr.map((stake) => {
      const posId = stake.positionId ?? stake.position_id
      const position = positionsArr.find((p) => (p.id ?? p.position_id) === posId)
      return {
        ...stake,
        title: position ? (position.title ?? position.name) : `Position #${posId ?? 'Unbekannt'}`
      }
    })
    return { ...bet, enrichedStakes }
  })
})

// Berechnete Eigenschaft, die nur die aktiven Wetten aus der angereicherten Liste filtert.
const activeBets = computed(() => {
  return enrichedMyBets.value.filter(bet => !bet.settled);
});

// Berechnete Eigenschaft, die nur die abgeschlossenen Wetten aus der angereicherten Liste filtert.
const finishedBets = computed(() => {
  return enrichedMyBets.value.filter(bet => bet.settled);
});

/**
 * Lädt die Wetten des aktuellen Benutzers vom Backend.
 */
const loadMyBets = async () => {
  try {
    const res = await getMyBets()
    myBets.value = Array.isArray(res?.answer) ? res.answer : []
  } catch (e) {
    myBets.value = []
  } finally {
    betsLoading.value = false
  }
}

// Zustand für den Avatar-Upload
const avatarProgress = ref(0)
const uploadingAvatar = ref(false)

/**
 * Handler, der aufgerufen wird, wenn der Benutzer eine neue Avatar-Datei auswählt.
 * Startet den Upload-Prozess.
 * @param {Event} event - Das 'change'-Event des Datei-Inputs.
 */
const handleAvatarSelected = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  avatarPreview.value = URL.createObjectURL(file)
  avatarProgress.value = 0
  uploadingAvatar.value = true
  clearMessages()
  try {
    const res = await uploadFile(file, {
      isAvatar: true,
      onProgress: (percent) => avatarProgress.value = percent})
    console.log("Avatar Upload:", res);
    successMessage.value = 'Avatar erfolgreich hochgeladen.'
    await refreshAuth({ force: true })
  } catch (e) {
    actionError.value = e.message || 'Upload fehlgeschlagen'
  } finally {
    uploadingAvatar.value = false
  }
}

/**
 * Fallback-Handler für Bild-Ladefehler.
 * @param {Event} e - Das 'error'-Event des <img>-Tags.
 */
const onAvatarError = (e) => {
  console.log("Show Avatr Fehler:", e);
  onImageError(e)
}

// Lifecycle-Hooks, um die Wetten zu laden, sobald der Benutzer verfügbar ist.
onMounted(async () => {
  if (ready.value && user.value) await loadMyBets()
})
watch(ready, (val) => {
  if (val && user.value) loadMyBets()
})

/**
 * Setzt alle Erfolgs- und Fehlermeldungen in der UI zurück.
 */
const clearMessages = () => {
  actionError.value = ''
  successMessage.value = ''
}

/**
 * Handler zum Aufladen des Demo-Guthabens.
 * @param {number} amount - Der aufzuladende Betrag.
 */
const addBalance = async (amount) => {
  clearMessages()

  try {
    const result = await balanceAufladen(amount)

    if (result?.success) {
      if (user.value) user.value.balance = result?.answer?.balance ?? user.value.balance;

      successMessage.value = `${amount} € Demo-Guthaben wurden hinzugefügt.`
      await refreshAuth({ force: true })
    } else {
      actionError.value = result?.message || 'Fehler beim Aufladen!'
    }
  } catch (e) {
    actionError.value = e.message || 'Fehler beim Aufladen!'
  }
}

/**
 * Handler zum Ändern des Passworts.
 */
const handleChangePassword = async () => {
  clearMessages()
  saving.value = true

  try {
    await changePassword(passwordForm.currentPassword, passwordForm.newPassword)
    successMessage.value = 'Passwort wurde geändert.'
    passwordForm.currentPassword = ''
    passwordForm.newPassword = ''
  } catch (e) {
    actionError.value = e.message || 'Passwort konnte nicht geändert werden.'
  } finally {
    saving.value = false
  }
}

/**
 * Handler zum Ändern der E-Mail-Adresse.
 */
const handleChangeEmail = async () => {
  clearMessages()
  saving.value = true

  try {
    await changeEmail(emailForm.newEmail, emailForm.password)

    successMessage.value = 'E-Mail wurde geändert.'
    emailForm.newEmail = ''
    emailForm.password = ''

    await refreshAuth({ force: true })
  } catch (e) {
    actionError.value = e.message || 'E-Mail konnte nicht geändert werden.'
  } finally {
    saving.value = false
  }
}

/**
 * Gibt den Status einer Wette als Objekt mit Schlüssel und Label zurück.
 * @param {object} bet - Das Wett-Objekt.
 * @returns {{key: string, label: string}}
 */
const getBetStatus = (bet) => {
  if (bet.settled) return { key: 'settled', label: 'Verrechnet' }
  if (bet.running) return { key: 'running', label: 'Läuft noch' }
  return { key: 'expired', label: 'Wettzeit vorbei' }
}

/**
 * Handler zum Löschen des Benutzerkontos.
 */
const handleDeleteAccount = async () => {
  clearMessages()

  if (deleteForm.confirmText !== 'DELETE') {
    actionError.value = 'Bitte zur Bestätigung DELETE eingeben.'
    return
  }

  const ok = window.confirm('Wirklich Konto dauerhaft löschen?')
  if (!ok) return

  saving.value = true

  try {
    await deleteAccount(deleteForm.password)

    successMessage.value = 'Konto wurde gelöscht.'
    await logout()
    router.push('/login')
  } catch (e) {
    actionError.value = e.message || 'Konto konnte nicht gelöscht werden.'
  } finally {
    saving.value = false
  }
}

/**
 * Handler, um den Einsatz auf eine Position zu verdoppeln (wird von BetMyStakesTable ausgelöst).
 * @param {object} bet - Das Wett-Objekt.
 * @param {object} entry - Der spezifische Einsatz, der verdoppelt werden soll.
 */
const handleIncreaseStake = async (bet, entry) => {
  clearMessages()

  try {
    const posId = entry.positionId ?? entry.position_id
    const amount = Number(entry.balance ?? entry.amount ?? 0)

    if (!bet?.id || !posId || !amount) return

    await placeBet(bet.id, posId, amount)
    successMessage.value = 'Einsatz erneut gesetzt.'
    await refreshAuth()
    await loadMyBets()
  } catch (e) {
    actionError.value = e.message || 'Verdoppeln fehlgeschlagen.'
  }
}
</script>

<style lang="scss" scoped>
.me-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 5rem;
}

h1 {
  margin-bottom: 1.5rem;
  color: $secondary-color;
}

.profile-card {
  background: $card-background;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  margin-bottom: 1.5rem;

  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: $secondary-color;
  }
}

.profile-header-card {
  padding-bottom: 1.25rem;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.avatar-wrap {
  position: relative;
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid $border-color;
  cursor: pointer;
  &:hover .avatar-overlay {
    opacity: 1;
  }
}

.avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.avatar-upload-btn {
  width: auto;
  padding: 0.55rem 0.9rem;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: #111;
  font-weight: 700;
  cursor: pointer;
}

.avatar-input {
  display: none;
}

.profile-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.profile-item {
  padding: 1rem;
  border: 1px solid $border-color;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);

  .label {
    display: block;
    font-size: 0.85rem;
    opacity: 0.75;
    margin-bottom: 0.35rem;
  }

  .value {
    font-size: 1.05rem;
    font-weight: 600;
  }
}

.profile-item.compact {
  padding: 0.85rem 1rem;
  margin: 0;
}

.demo-balance-box {
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid $border-color;
  background: rgba(255, 255, 255, 0.03);

  h3 {
    margin-top: 0;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 1rem;
    opacity: 0.8;
  }
}

.demo-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.demo-button {
  width: auto;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-weight: 600;
  transition: 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &.danger {
    background: #ff4d4f;
    color: white;
  }
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
  text-align: left;

  label {
    display: block;
    margin-bottom: 0.45rem;
    font-weight: 600;
    color: $text-color;
  }

  input {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid $border-color;
    border-radius: 8px;
    box-sizing: border-box;
    background: transparent;
    color: $text-color;

    &:focus {
      outline: none;
      border-color: $primary-color;
      box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
    }
  }
}

button {
  width: 100%;
  padding: 0.85rem 1rem;
  border: none;
  border-radius: 8px;
  background: $primary-color;
  color: white;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: $primary-color-hover;
  }
}

.danger-card {
  border-color: rgba(220, 53, 69, 0.35);
}

button.danger {
  background: #dc3545;

  &:hover:not(:disabled) {
    background: #c82333;
  }
}

.state-box,
.message {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 10px;
}

.state-box {
  background: rgba(255, 255, 255, 0.04);
}

.state-box.error,
.message.error {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.message.success {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}

.bets-list {
  display: grid;
  gap: 1rem;
}

.bets-section {
  & + .bets-section {
    margin-top: 2rem;
  }
}

.bet-item {
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 1rem;
}

.bet-title-link {
  text-decoration: none;
  color: inherit;

  &:hover h3 {
    color: $primary-color;
  }
}

.bet-top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: start;

  h3 {
    margin: 0;
  }
}

.bet-status {
  font-size: 0.85rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
}
.bet-status.active {
  background: rgba($primary-color, 0.12);
  color: $primary-color;
}
.bet-status.settled {
  background: rgba($accent-color, 0.12);
  color: $accent-color;
}
.bet-status.running {
  background: rgba($primary-color, 0.12);
  color: $primary-color;
}
.bet-status.expired {
  background: rgba(255, 193, 7, 0.14);
  color: #a36b00;
}
.bet-status.settled {
  background: rgba($accent-color, 0.12);
  color: $accent-color;
}

.bet-desc {
  margin: 0.75rem 0;
  opacity: 0.9;
}

.bet-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.95rem;
}
.avatar-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.4rem;
  background: rgba(0,0,0,0.6);
  color: white;
  font-size: 0.75rem;
  text-align: center;
}

.avatar-progress .bar {
  height: 4px;
  background: rgba(255,255,255,0.2);
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.25rem;
}

.avatar-progress .fill {
  height: 100%;
  background: #4caf50;
  width: 0%;
  transition: width 0.1s linear;
}
</style>